import React from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAsyncData } from "@/hooks/useAsyncData";
import { Money, StoreDatePicker, LoadErrorState, LoadingState, EmptyState, StatusBadge } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate, fmtDateTime } from "@/lib/api";
import { Search, CheckCircle2, AlertTriangle, SendHorizontal } from "lucide-react";

const Row = ({ label, paise, sub, strong, neg, testId }) => (
  <div className={`flex items-start justify-between gap-3 py-1.5 ${strong ? "border-t font-semibold" : ""}`} data-testid={testId}>
    <div className="min-w-0">
      <p className="text-sm">{label}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
    <Money paise={neg ? -paise : paise} className={neg ? "money-neg" : ""} />
  </div>
);

export default function BalanceTracePage() {
  const { storeId, date } = useApp();

  const q = useAsyncData(
    () => {
      if (!storeId || !date) return Promise.resolve(null);
      return api.get("/reports/balance-trace", { params: { store_id: storeId, business_date: date } })
        .then((r) => r.data);
    },
    [storeId, date]
  );

  const header = (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div>
        <h1 className="font-display text-xl font-semibold arch-underline">Balance trace</h1>
        <p className="text-sm text-muted-foreground">
          Read-only walk of one store-day's cash chain — previous close, HQ sends, opening, movements, count, variance, next opening
        </p>
      </div>
      <StoreDatePicker />
    </div>
  );

  if (q.error) return <div className="space-y-5">{header}<LoadErrorState error={q.error} onRetry={q.reload} title="Could not load the balance trace" /></div>;
  if (q.loading) return <div className="space-y-5">{header}<LoadingState /></div>;
  if (!q.data) return <div className="space-y-5">{header}<EmptyState icon={Search} title="Pick a store and date" /></div>;

  const { chain: c, screens, divergences, formula } = q.data;
  const m = c.movements;

  return (
    <div className="space-y-5">
      {header}

      {divergences.length === 0 ? (
        <div className="rounded-lg border border-[hsl(var(--success))]/45 bg-[hsl(var(--success))]/10 px-4 py-3 flex items-start gap-2 text-sm"
          data-testid="trace-clean" role="status">
          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))] mt-0.5 shrink-0" />
          <p>Every screen agrees on this day's figures. <span className="text-muted-foreground">{formula}</span></p>
        </div>
      ) : (
        <div className="rounded-lg border border-[hsl(var(--danger))]/45 bg-[hsl(var(--danger))]/8 px-4 py-3 space-y-1 text-sm"
          data-testid="trace-divergence" role="alert">
          <p className="font-semibold text-[hsl(var(--danger))] flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> First divergence
          </p>
          {divergences.map((d, i) => <p key={i} className="text-xs">{i + 1}. {d}</p>)}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <Card className="rounded-lg"><CardContent className="p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Where the opening came from</p>
          {c.prev_close ? (
            <>
              <Row label={`Counted close of ${fmtDate(c.prev_close.business_date)}`}
                sub={c.prev_close.finalized ? "finalized" : "counted but not finalized — carry is provisional"}
                paise={c.prev_close.counted_paise} testId="trace-prev-close" />              {(c.prev_close.sends || []).map((t) => (
                <Row key={t.id} label="Sent to HQ from that close"
                  sub={`${t.by || "—"} · entered ${fmtDateTime(t.entered_at)}${t.note ? ` · ${t.note}` : ""}`}
                  paise={t.amount_paise} neg testId={`trace-send-${t.id.slice(0, 6)}`} />
              ))}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No earlier counted close — this store's carry starts at nil.</p>
          )}
          <Row label="Carried opening" paise={c.carried_opening_paise} strong testId="trace-carry" />
          {!!c.opening_adjustment_paise && (
            <Row label="Admin opening adjustment (audited)" paise={c.opening_adjustment_paise}
              sub={c.opening_adjustment?.reason} testId="trace-adjustment" />
          )}
          <Row label="Effective opening" paise={c.effective_opening_paise} strong testId="trace-effective-opening" />
          {(c.missing_counts || []).length > 0 && (
            <div className="mt-2 rounded border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/8 p-2 text-[11px] space-y-0.5"
              data-testid="trace-missing-counts">
              <p className="font-semibold text-[hsl(var(--danger))]">Carry blocked — uncounted trading day(s)</p>
              {c.missing_counts.map((g) => (
                <p key={g.business_date}>
                  {fmtDate(g.business_date)}: {g.bill_count} bill(s), net cash <Money paise={g.net_cash_paise} className="text-foreground" /> — no closing count
                </p>
              ))}
            </div>
          )}
          {c.carry_conflict && (
            <p className="mt-2 text-[11px] text-[hsl(var(--danger))] font-semibold" data-testid="trace-carry-conflict">
              The previous close has less counted cash than the HQ sends linked to it — an audited correction is required.
            </p>
          )}
        </CardContent></Card>

        <Card className="rounded-lg"><CardContent className="p-4">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">The day itself</p>
          <Row label="Cash received on bills" paise={m.cash_from_bills_paise} />
          <Row label="Excess returned in cash" paise={m.cash_excess_returned_paise} neg />
          <Row label="Other cash receipts" paise={m.adj_cash_receipts_paise} />
          <Row label="Cash deductions" paise={m.adj_cash_deductions_paise} neg />
          <Row label="Cash expenses" paise={m.cash_expenses_paise} neg />
          <Row label="Expected cash before HQ send" paise={c.expected_pre_hq_paise} strong testId="trace-expected" />
          <Row label="Actual counted cash (pre-HQ)" paise={c.actual_pre_hq_paise}
            sub={c.count_stale ? `counted when ${(c.count_submitted_expected_paise / 100).toLocaleString("en-IN")} was expected — needs re-confirmation` : undefined}
            testId="trace-counted" />
          <Row label="Variance (shortage / excess)" paise={c.variance_paise} strong testId="trace-variance" />
        </CardContent></Card>
      </div>

      <Card className="rounded-lg"><CardContent className="p-4">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">After the count</p>
        {(c.hq_sends_from_this_close || []).length === 0 && (
          <p className="text-sm text-muted-foreground">No cash has been sent to HQ from this close yet.</p>
        )}
        {(c.hq_sends_from_this_close || []).map((t) => (
          <Row key={t.id} label={<span className="flex items-center gap-1.5"><SendHorizontal className="h-3.5 w-3.5" />Sent to HQ</span>}
            sub={`${t.by || "—"} · entered ${fmtDateTime(t.entered_at)} (entry date ${fmtDate(t.entered_on)})${t.note ? ` · ${t.note}` : ""}`}
            paise={t.amount_paise} neg testId={`trace-out-${t.id.slice(0, 6)}`} />
        ))}
        <Row label="Stays for the next day's opening" paise={c.next_opening_paise} strong testId="trace-next-opening" />
        {c.next_day && (
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="trace-next-day">
            {fmtDate(c.next_day.business_date)} currently opens with <Money paise={c.next_day.opening_paise} className="text-foreground" /> ({c.next_day.status})
          </p>
        )}
      </CardContent></Card>

      {(c.discrepancies || []).length > 0 && (
        <Card className="rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b"><p className="font-display font-semibold text-sm">Discrepancy and staff allocation</p></div>
          <Table className="table-compact">
            <TableHeader><TableRow>
              <TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead>
              <TableHead>Assigned to</TableHead><TableHead className="text-right">Unallocated</TableHead>
              <TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {c.discrepancies.map((d) => (
                <TableRow key={d.id} data-testid={`trace-disc-${d.id.slice(0, 6)}`}>
                  <TableCell className="capitalize text-xs">{d.type}</TableCell>
                  <TableCell className="amount-cell"><Money paise={d.amount_paise} /></TableCell>
                  <TableCell className="text-xs">
                    {(d.allocations || []).map((a) => `${a.cashier_name} ${(a.amount_paise / 100).toLocaleString("en-IN")}`).join(", ") || "—"}
                  </TableCell>
                  <TableCell className="amount-cell"><Money paise={d.unallocated_paise} /></TableCell>
                  <TableCell><StatusBadge status={d.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Card className="rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b"><p className="font-display font-semibold text-sm">What each screen shows for this day</p></div>
        <Table className="table-compact">
          <TableHeader><TableRow>
            <TableHead>Screen</TableHead>
            <TableHead className="text-right">Opening</TableHead>
            <TableHead className="text-right">Expected (pre-HQ)</TableHead>
            <TableHead className="text-right">Counted</TableHead>
            <TableHead className="text-right">Variance</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {[["Dashboard · Register · Day Close", screens.dashboard_register_day_close],
              ["Cash Count page", screens.cash_count_page],
              ["Saved count record", screens.stored_count_doc]].map(([label, v]) => (
              <TableRow key={label} data-testid={`trace-screen-${label.split(" ")[0].toLowerCase()}`}>
                <TableCell className="text-xs font-medium">{label}</TableCell>
                <TableCell className="amount-cell"><Money paise={v.opening_paise} /></TableCell>
                <TableCell className="amount-cell"><Money paise={v.expected_pre_hq_paise} /></TableCell>
                <TableCell className="amount-cell"><Money paise={v.actual_pre_hq_paise} /></TableCell>
                <TableCell className="amount-cell"><Money paise={v.variance_paise} colored signed /></TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell className="text-xs font-medium">HQ statement · next opening</TableCell>
              <TableCell colSpan={3} className="text-xs text-muted-foreground">carried forward after HQ sends</TableCell>
              <TableCell className="amount-cell"><Money paise={screens.hq_statement_next_opening_paise} /></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
